import { Box, Modal, ModalDialog } from "@mui/joy";
import { motion } from "framer-motion";

import { useVisualViewport } from "../../hooks/useVisualViewport";

/**
 * Переписка на телефоне — поверх списка чатов.
 *
 * БЕЗ ModalOverflow. Это важно: ModalOverflow — прокручиваемая обёртка
 * (position:absolute; inset:0; overflow:hidden auto). Полноэкранное окно
 * внутри неё получалось чуть выше контейнера, и прокручивалось ВСЁ окно
 * целиком: шапка и поле ввода уезжали вместе с лентой, а сверху и снизу
 * появлялись пустые поля, которые можно было оттянуть. Лента сообщений
 * должна быть единственным, что здесь прокручивается.
 *
 * Без AnimatePresence и exit-анимаций — по той же причине, что и раньше:
 * недоигравшая анимация ухода оставляла модалку в DOM вместе с блокировкой
 * прокрутки, и приложение выглядело зависшим.
 */
export default function ModalMessagePane({ children, modalMessage, setModalMessage }) {
    const { height: vvHeight, offsetTop } = useVisualViewport();

    // Высоту берём у видимой области, а не у экрана. 100dvh клавиатуру не
    // учитывает: окно остаётся во весь экран, клавиатура ложится поверх, и
    // поле ввода вместе с нижней частью ленты оказывается под ней — видно
    // только шапку. Сужая окно до visualViewport, получаем то самое
    // поведение мессенджеров: шапка сверху, поле ввода прямо над
    // клавиатурой, лента — между ними.
    //
    // offsetTop компенсирует сдвиг документа, который iOS делает при фокусе:
    // без него верх чата уезжает за край экрана.
    // bottom: 'auto' обязателен. layout="fullscreen" растягивает окно через
    // inset: 0, то есть задаёт и top, и bottom; заданная высота при этом
    // формально побеждает, но полагаться на разрешение конфликта не стоит —
    // проще снять лишнюю привязку явно.
    const box = vvHeight
        ? {
            position: 'fixed',
            top: `${offsetTop}px`,
            bottom: 'auto',
            height: `${vvHeight}px`,
            maxHeight: `${vvHeight}px`,
        }
        : { height: '100dvh', maxHeight: '100dvh' };

    return (
        <Modal
            open={!!modalMessage?.open}
            sx={{ zIndex: 10012 }}
            slotProps={{
                backdrop: { sx: { backdropFilter: 'blur(8px)' } },
            }}
        >
            <ModalDialog
                layout="fullscreen"
                size="lg"
                sx={{
                    p: 0,
                    // Высота и сдвиг — от visualViewport, с откатом на 100dvh
                    // там, где его нет. dvh сам по себе не спасает: он учитывает
                    // сворачивающуюся адресную строку, но не клавиатуру.
                    ...box,
                    overflow: 'hidden',
                    // Тянуть само окно нечего — прокручивается только лента.
                    overscrollBehavior: 'none',
                    // Высота меняется рывком при выезде клавиатуры; короткий
                    // переход превращает рывок в движение.
                    transition: 'height .18s ease-out, top .18s ease-out',
                }}
            >
                <Box
                    component={motion.div}
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.18, ease: 'easeOut' }}
                    sx={{
                        // Внутри — лента со своей прокруткой, поэтому цепочку
                        // flex рвать нельзя.
                        display: 'flex', flexDirection: 'column',
                        flex: 1, minHeight: 0, width: '100%',
                        overflow: 'hidden',
                    }}
                >
                    {children}
                </Box>
            </ModalDialog>
        </Modal>
    );
}
